import { toTypedSchema } from '@vee-validate/zod';
import * as zod from 'zod';
import { ZodTypeAny } from 'zod';
import RuleType from '../interfaces/RuleType';

const MAX_FILE_SIZE = 2104704;
export const defaultResponse = 'A response is required';
export const errorMessageGeneration = (minCharacters: number, maxCharacters: number, defaultMessage: string = defaultResponse) => new Map([
  ['default', defaultMessage],
  ['minimum:string', `Minimum Character Length is ${minCharacters}`],
  ['maximum:string', `Maximum Character Length is ${maxCharacters}`],
  ['minimum:number', `Minimum Characters is ${minCharacters}`],
  ['maximum:number', `Maximum Characters is ${maxCharacters}`],
  ['max_file_size', `Maximum file size is ${Math.floor(MAX_FILE_SIZE / 1000000)}MB`],
]);

export const uniqueRulesRaw = (itemName: string, array: string[], ignoredValue: string = '', required: boolean = true, defaultMessage: string = defaultResponse) => {
  const errorMessageMap = errorMessageGeneration(1, 100, defaultMessage);
  const values = array.map((i) => i.toLowerCase()).filter((item) => item !== ignoredValue.toLowerCase());

  if (required) {
    return zod.string({ required_error: errorMessageMap.get('default') })
      .trim()
      .min(1, { message: errorMessageMap.get('default') })
      .max(100, { message: errorMessageMap.get('maximum:number') })
      .refine((item) => !values.includes(item.toLowerCase().trim()), {
        message: `${itemName} exists`,
      });
  }
  return zod.string({ required_error: errorMessageMap.get('default') })
    .max(100, { message: errorMessageMap.get('maximum:number') })
    .refine((item) => !values.includes(item.toLowerCase().trim()), {
      message: `${itemName} exists`,
    });

};
export const uniqueRules = (itemName: string, array: string[], ignoredValue: string = '', required: boolean = true, defaultMessage: string = defaultResponse) => toTypedSchema(
  uniqueRulesRaw(itemName, array, ignoredValue, required, defaultMessage),
);

export const numericString = (schema: ZodTypeAny) => zod.preprocess((a) => {
  if (typeof a === 'string' && a !== '') {
    return a;
  } if (typeof a === 'number') {
    return a.toString();
  }
  return undefined;
}, schema);

const customErrorMap: zod.ZodErrorMap = (issue, ctx) => {
  if (issue.code === zod.ZodIssueCode.custom) {
    return { message: defaultResponse };
  }

  return { message: ctx.defaultError };
};

zod.setErrorMap(customErrorMap);

export const generateRules = (
  type: RuleType,
  required: boolean = false,
  minCharacters: number = 1,
  maxCharacters: number = 255,
  defaultMessage: string | null = null,
) => {
  const errorMessageMap = errorMessageGeneration(minCharacters, maxCharacters);

  let message = minCharacters > 1 ? errorMessageMap.get('minimum:string') : errorMessageMap.get('default');

  if (defaultMessage) {
    message = defaultMessage;
  }

  if (type === RuleType.Boolean) {
    if (required) {
      return toTypedSchema(zod.boolean({
        required_error: errorMessageMap.get('default'),
        invalid_type_error: errorMessageMap.get('default'),
      }));
    }
    return toTypedSchema(zod.boolean().or(zod.literal('')).or(zod.literal(null)));
  }

  if (type === RuleType.String) {
    if (required) {
      return toTypedSchema(
        zod.string({ required_error: errorMessageMap.get('default') })
          .trim()
          .min(minCharacters, { message })
          .max(maxCharacters, { message: errorMessageMap.get('maximum:string') }));
    }
    return toTypedSchema(
      zod.string()
        .trim()
        .min(minCharacters, { message })
        .max(maxCharacters, { message: errorMessageMap.get('maximum:string') })
        .or(zod.literal(''))
    );
  }
  if (type === RuleType.Email) {
    if (required) {
      return toTypedSchema(
        zod
          .string({ required_error: errorMessageMap.get('default') })
          .trim()
          .min(1, errorMessageMap.get('default'))
          .email(`${defaultMessage ?? 'Must be a valid email'}`)
        ,
      );
    }
    return toTypedSchema(
      zod.string()
        .trim()
        .email(`${defaultMessage ?? 'Must be a valid email'}`)
        .or(zod.literal(''))
    );
  }

  if (type === RuleType.URL) {
    if (required) {
      return toTypedSchema(
        zod.string()
          .trim()
          .min(1, { message: errorMessageMap.get('default') })
          .url(`${defaultMessage ?? 'Invalid url'}`));
    }
    return toTypedSchema(
      zod.string()
        .trim()
        .url(`${defaultMessage ?? 'Invalid url'}`)
        .or(zod.literal(''))
    );
  }

  if (type === RuleType.Number) {
    if (required) {
      return toTypedSchema(numericString(
        zod.string({ required_error: errorMessageMap.get('default') })
          .trim()
          .min(minCharacters, { message: errorMessageMap.get('minimum:number') })
          .max(maxCharacters, { message: errorMessageMap.get('maximum:number') }),
      ));
    }
    return toTypedSchema(zod.string().trim().or(zod.literal('')));
  }

  if (type === RuleType.File) {
    if (required) {
      return toTypedSchema(
        zod.instanceof(File, {}).refine((file: File) => file.size <= MAX_FILE_SIZE, errorMessageMap.get('max_file_size')),
      );
    }
    return toTypedSchema(
      zod.instanceof(File, {})
        .refine((file: File) => file.size <= MAX_FILE_SIZE, errorMessageMap.get('max_file_size'))
        .optional()
        .or(zod.literal('')),
    );
  }

  return toTypedSchema(zod.string().trim());
};
